import { ProjectParameters } from './projectParameters';
import { getAllLots } from './lotsData';

export interface RatioData {
  id: string;
  projet: string;
  typeProjet: string;
  surface: number;
  coutTotal: number;
  ratioM2: number;
  lots: LotRatio[];
  dateCreation: string;
  statut: string;
  parameters: ProjectParameters;
  unitesDisponibles: string[];
  nbLogements?: number;
  nbParkings?: number;
  m3Beton?: number;
  nbLits?: number;
  m2Plancher?: number;
  nbChambres?: number;
}

type BaseProject = Omit<RatioData, 'unitesDisponibles' | 'nbLogements' | 'nbParkings' | 'm3Beton' | 'nbLits' | 'm2Plancher' | 'nbChambres'>;

function enrichWithUnits(p: BaseProject): RatioData {
  const units: string[] = ['m2-sdp'];
  const extras: Partial<RatioData> = {};
  const surface = p.surface;
  const isNeuf = p.parameters.ModaliteProgramme === 'Neuf';

  switch (p.typeProjet) {
    case 'Résidentiel': {
      const nbLogements = Math.max(1, Math.round(surface / 70));
      extras.nbLogements = nbLogements;
      extras.nbParkings = Math.round(nbLogements * 0.9);
      extras.m2Plancher = Math.round(surface * 0.88);
      units.push('logements', 'parkings', 'm2-plancher');
      if (isNeuf) {
        extras.m3Beton = Math.round(surface * 0.35);
        units.push('m3-beton');
      }
      break;
    }
    case 'Tertiaire': {
      extras.m2Plancher = Math.round(surface * 0.88);
      extras.nbParkings = Math.round(surface / 80);
      units.push('m2-plancher', 'parkings');
      if (isNeuf) {
        extras.m3Beton = Math.round(surface * 0.30);
        units.push('m3-beton');
      }
      break;
    }
    case 'Santé': {
      const nbLits = Math.max(1, Math.round(surface / 60));
      extras.nbLits = nbLits;
      extras.nbChambres = Math.max(1, Math.round(nbLits * 0.7));
      extras.m2Plancher = Math.round(surface * 0.88);
      extras.nbParkings = Math.round(nbLits * 0.3);
      units.push('lits', 'chambres', 'm2-plancher', 'parkings');
      break;
    }
    case 'Éducation': {
      extras.m2Plancher = Math.round(surface * 0.88);
      extras.nbParkings = Math.round(surface / 120);
      units.push('m2-plancher', 'parkings');
      break;
    }
    case 'Commerce': {
      extras.m2Plancher = Math.round(surface * 0.92);
      extras.nbParkings = Math.round(surface / 50);
      units.push('m2-plancher', 'parkings');
      break;
    }
    case 'Hôtel': {
      const nbChambres = Math.max(1, Math.round(surface / 45));
      extras.nbChambres = nbChambres;
      extras.nbLits = Math.round(nbChambres * 1.4);
      extras.nbParkings = Math.round(nbChambres * 0.4);
      extras.m2Plancher = Math.round(surface * 0.85);
      units.push('chambres', 'lits', 'parkings', 'm2-plancher');
      break;
    }
    case 'Industriel':
    case 'Industrie': {
      extras.m2Plancher = Math.round(surface * 0.95);
      extras.m3Beton = Math.round(surface * 0.50);
      units.push('m2-plancher', 'm3-beton');
      break;
    }
  }

  return { ...p, ...extras, unitesDisponibles: units };
}

export interface LotRatio {
  id: string;
  nom: string;
  categorie: string;
  surface: number;
  cout: number;
  ratioM2: number;
  pourcentageProjet: number;
  entreprise?: string;
  enfants?: LotRatio[];
}

const ENTREPRISES_POOL = [
  'Bouygues Bâtiment', 'Eiffage Construction', 'Vinci Construction',
  'Demathieu Bard', 'Léon Grosse', 'Sogea', 'Spie Batignolles',
  'Rabot Dutilleul', 'Fayat', 'Colas Bâtiment',
];

// ─── Hiérarchie interne : ensembles et sous-ensembles ───────────────────────
interface ChildDef {
  nom: string;
  fraction: number; // fraction du coût parent
  enfants?: ChildDef[];
}

const LOT_CHILDREN_STRUCTURE: Record<string, ChildDef[]> = {
  'CVC': [
    { nom: 'Chauffage', fraction: 0.40, enfants: [
      { nom: 'Production Chauffage (chaudières / échangeurs)', fraction: 0.45 },
      { nom: 'Distribution Chauffage', fraction: 0.35 },
      { nom: 'Émetteurs', fraction: 0.20 },
    ]},
    { nom: 'Ventilation', fraction: 0.35, enfants: [
      { nom: "Centrales de Traitement d'Air (CTA)", fraction: 0.60 },
      { nom: 'VMC et Extracteurs', fraction: 0.40 },
    ]},
    { nom: 'Climatisation', fraction: 0.25, enfants: [
      { nom: 'Groupes Froids / PAC', fraction: 0.55 },
      { nom: 'Diffusion Froid', fraction: 0.45 },
    ]},
  ],
  'Façades': [
    { nom: 'Bardage et ITE', fraction: 0.50, enfants: [
      { nom: 'Ossature Façade', fraction: 0.30 },
      { nom: 'Isolant Façade', fraction: 0.25 },
      { nom: 'Parement Extérieur', fraction: 0.45 },
    ]},
    { nom: 'Menuiseries Façade', fraction: 0.35 },
    { nom: 'Étanchéité Façade', fraction: 0.15 },
  ],
  'GO y compris charpente hors infra': [
    { nom: 'Superstructure Béton', fraction: 0.55 },
    { nom: 'Charpente Métallique', fraction: 0.30 },
    { nom: 'Éléments Préfabriqués', fraction: 0.15 },
  ],
  'CFO-CFA': [
    { nom: 'Courants Forts (CFO)', fraction: 0.58, enfants: [
      { nom: 'Tableaux et Distribution', fraction: 0.40 },
      { nom: 'Câblage et Appareillage', fraction: 0.35 },
      { nom: 'Éclairage', fraction: 0.25 },
    ]},
    { nom: 'Courants Faibles (CFA)', fraction: 0.42, enfants: [
      { nom: 'VDI / Réseaux Informatiques', fraction: 0.50 },
      { nom: 'Sécurité Incendie (SSI)', fraction: 0.30 },
      { nom: "Contrôle d'Accès et Vidéo", fraction: 0.20 },
    ]},
  ],
  'Plomberie': [
    { nom: 'Alimentation Eau Froide', fraction: 0.35 },
    { nom: 'Production ECS', fraction: 0.30 },
    { nom: 'Évacuations et EP', fraction: 0.25 },
    { nom: 'Appareils Sanitaires', fraction: 0.10 },
  ],
};

const createLotChildren = (
  parentCout: number,
  coutTotal: number,
  surface: number,
  categorie: string,
  childDefs: ChildDef[],
  projetIdNum: number,
  seedBase: number,
  entreprise?: string
): LotRatio[] =>
  childDefs.map((childDef, index) => {
    const seed = (projetIdNum * 13 + seedBase * 7 + (index + 1) * 23) % 41;
    const varianceFactor = 0.80 + seed * 0.01;
    const coutEnfant = parentCout * childDef.fraction * varianceFactor;
    return {
      id: `${seedBase}-${index + 1}`,
      nom: childDef.nom,
      categorie,
      surface,
      cout: Math.round(coutEnfant),
      ratioM2: Math.round(coutEnfant / surface),
      pourcentageProjet: Math.round((coutEnfant / coutTotal) * 10000) / 100,
      entreprise,
      enfants: childDef.enfants
        ? createLotChildren(coutEnfant, coutTotal, surface, categorie, childDef.enfants, projetIdNum, seedBase * 100 + index + 1, entreprise)
        : undefined,
    };
  });

// Fonction utilitaire pour créer des lots avec répartition cohérente et variabilité
const createProjectLots = (surface: number, coutTotal: number, typeProjet: string, projetId: string): LotRatio[] => {
  const allLots = getAllLots();
  const projectLots: LotRatio[] = [];
  
  // Répartition par type de projet
  let repartition: { [key: string]: number } = {};
  
  if (typeProjet === 'Tertiaire' || typeProjet === 'Santé' || typeProjet === 'Éducation') {
    repartition = {
      'preparation': 0.05,      // 5% - Préparation
      'structure': 0.35,        // 35% - Structure
      'enveloppe': 0.25,       // 25% - Enveloppe
      'technique': 0.25,       // 25% - Technique
      'second-oeuvre': 0.08,   // 8% - Second œuvre
      'exterieur': 0.02        // 2% - Extérieur
    };
  } else if (typeProjet === 'Logement' || typeProjet === 'Résidentiel') {
    repartition = {
      'preparation': 0.03,      // 3% - Préparation
      'structure': 0.40,        // 40% - Structure
      'enveloppe': 0.25,       // 25% - Enveloppe
      'technique': 0.20,       // 20% - Technique
      'second-oeuvre': 0.10,   // 10% - Second œuvre
      'exterieur': 0.02        // 2% - Extérieur
    };
  } else if (typeProjet === 'Commerce') {
    repartition = {
      'preparation': 0.04,      // 4% - Préparation
      'structure': 0.30,        // 30% - Structure
      'enveloppe': 0.30,       // 30% - Enveloppe
      'technique': 0.25,       // 25% - Technique
      'second-oeuvre': 0.08,   // 8% - Second œuvre
      'exterieur': 0.03        // 3% - Extérieur
    };
  } else if (typeProjet === 'Industrie') {
    repartition = {
      'preparation': 0.05,      // 5% - Préparation
      'structure': 0.45,        // 45% - Structure
      'enveloppe': 0.25,       // 25% - Enveloppe
      'technique': 0.20,       // 20% - Technique
      'second-oeuvre': 0.03,   // 3% - Second œuvre
      'exterieur': 0.02        // 2% - Extérieur
    };
  } else if (typeProjet === 'Hôtel') {
    repartition = {
      'preparation': 0.04,
      'structure': 0.32,        // 32% - Structure
      'enveloppe': 0.22,       // 22% - Enveloppe
      'technique': 0.28,       // 28% - CVC/CFO/plomberie plus importants
      'second-oeuvre': 0.12,   // 12% - Finitions et agencement
      'exterieur': 0.02        // 2% - Extérieur
    };
  } else {
    // Répartition par défaut
    repartition = {
      'preparation': 0.04,
      'structure': 0.38,
      'enveloppe': 0.25,
      'technique': 0.23,
      'second-oeuvre': 0.08,
      'exterieur': 0.02
    };
  }

  let lotId = 1;
  const projetIdNum = parseInt(projetId);

  // Créer les lots pour chaque catégorie avec variabilité
  Object.entries(repartition).forEach(([categorieId, pourcentage]) => {
    const categorieLots = allLots.filter(lot => lot.categorie === categorieId);
    const coutCategorie = coutTotal * pourcentage;
    
    // Déterminer quels lots inclure selon le type de projet et l'ID du projet
    let lotsToInclude = [...categorieLots];
    
    // Variabilité selon le type de projet
    if (categorieId === 'technique') {
      if (typeProjet === 'Logement' || typeProjet === 'Résidentiel') {
        // Logement : pas d'ascenseurs pour les petits projets
        if (surface < 3000) {
          lotsToInclude = lotsToInclude.filter(lot => lot.nom !== 'Ascenseurs');
        }
      } else if (typeProjet === 'Industrie') {
        // Industrie : pas d'ascenseurs, moins de CVC
        lotsToInclude = lotsToInclude.filter(lot => lot.nom !== 'Ascenseurs');
        if (surface < 8000) {
          lotsToInclude = lotsToInclude.filter(lot => lot.nom !== 'CVC');
        }
      }
    }
    
    if (categorieId === 'exterieur') {
      if (typeProjet === 'Industrie') {
        // Industrie : pas d'espaces verts
        lotsToInclude = lotsToInclude.filter(lot => lot.nom !== 'Espaces Verts');
      } else if (typeProjet === 'Commerce') {
        // Commerce : plus de VRD, moins d'espaces verts (déterministe)
        if (projetIdNum % 3 !== 0) {
          lotsToInclude = lotsToInclude.filter(lot => lot.nom !== 'Espaces Verts');
        }
      }
    }
    
    if (categorieId === 'second-oeuvre') {
      if (typeProjet === 'Industrie') {
        // Industrie : pas de faux plafonds, moins de finitions
        lotsToInclude = lotsToInclude.filter(lot => 
          !['Second œuvre : faux plafonds', 'Second œuvre : agencement, plâtrerie, cloisonnement'].includes(lot.nom)
        );
      } else if (typeProjet === 'Logement' || typeProjet === 'Résidentiel') {
        // Logement : pas de faux plancher pour les petits projets
        if (surface < 2000) {
          lotsToInclude = lotsToInclude.filter(lot => lot.nom !== 'Second œuvre : faux plancher');
        }
      }
    }
    
    if (categorieId === 'enveloppe') {
      if (typeProjet === 'Industrie') {
        // Industrie : moins de menuiseries extérieures
        if (surface < 10000) {
          lotsToInclude = lotsToInclude.filter(lot => lot.nom !== 'Menuiseries extérieures');
        }
      }
    }
    
    // Variabilité selon l'ID du projet (pour plus de diversité)
    if (projetIdNum % 3 === 0) {
      // 1/3 des projets n'ont pas d'occultations
      if (categorieId === 'enveloppe') {
        lotsToInclude = lotsToInclude.filter(lot => lot.nom !== 'Occultations');
      }
    }
    if (projetIdNum % 4 === 0) {
      // 1/4 des projets n'ont pas de plomberie (projets de rénovation)
      if (categorieId === 'technique') {
        lotsToInclude = lotsToInclude.filter(lot => lot.nom !== 'Plomberie');
      }
    }
    
    // S'assurer qu'il y a au moins un lot par catégorie
    if (lotsToInclude.length === 0) {
      lotsToInclude = [categorieLots[0]];
    }
    
    // Répartir le coût de la catégorie entre les lots inclus, avec variance déterministe ±20%
    lotsToInclude.forEach((lot, index) => {
      const seed = (projetIdNum * 17 + lotId * 31) % 41; // 0..40
      const varianceFactor = 0.80 + seed * 0.01; // 0.80 to 1.20
      const coutLot = (coutCategorie / lotsToInclude.length) * varianceFactor;
      const ratioM2 = Math.round(coutLot / surface);
      
      const childrenDef = LOT_CHILDREN_STRUCTURE[lot.nom];
      const entreprise = ENTREPRISES_POOL[(projetIdNum * 7 + lotId * 11) % ENTREPRISES_POOL.length];
      projectLots.push({
        id: `${lotId}`,
        nom: lot.nom,
        categorie: lot.categorie,
        surface: surface,
        cout: Math.round(coutLot),
        ratioM2: ratioM2,
        pourcentageProjet: Math.round((coutLot / coutTotal) * 100 * 100) / 100,
        entreprise,
        enfants: childrenDef
          ? createLotChildren(coutLot, coutTotal, surface, lot.categorie, childrenDef, projetIdNum, lotId, entreprise)
          : undefined,
      });

      lotId++;
    });
  });

  return projectLots;
};

const rawRatioData: BaseProject[] = [
  {
    id: '1',
    projet: 'Projet 1',
    typeProjet: 'Tertiaire',
    surface: 1251,
    coutTotal: 1876500,
    ratioM2: 1500,
    dateCreation: '2021',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Tertiaire',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Entreprise Générale',
      Gamme: 'Moyenne'
    },
    lots: createProjectLots(1251, 1876500, 'Tertiaire', '1')
  },
  {
    id: '2',
    projet: 'Projet 2',
    typeProjet: 'Résidentiel',
    surface: 3184,
    coutTotal: 4776000,
    ratioM2: 1500,
    dateCreation: '2021',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Logement',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Macro-Lot',
      Gamme: 'Haute'
    },
    lots: createProjectLots(3184, 4776000, 'Résidentiel', '2')
  },
  {
    id: '3',
    projet: 'Projet 3',
    typeProjet: 'Résidentiel',
    surface: 2529,
    coutTotal: 3793500,
    ratioM2: 1500,
    dateCreation: '2021',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Logement',
      ModaliteProgramme: 'Réhabilitation',
      DevolutonMarche: 'Corps d\'État Séparés',
      Gamme: 'Moyenne'
    },
    lots: createProjectLots(2529, 3793500, 'Résidentiel', '3')
  },
  {
    id: '4',
    projet: 'Résidence Les Jardins',
    typeProjet: 'Résidentiel',
    surface: 4500,
    coutTotal: 6750000,
    ratioM2: 1500,
    dateCreation: '2022',
    statut: 'En cours',
    parameters: {
      TypologieProgramme: 'Logement',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Entreprise Générale',
      Gamme: 'Très Haute'
    },
    lots: createProjectLots(4500, 6750000, 'Résidentiel', '4')
  },
  {
    id: '5',
    projet: 'Centre Commercial Lyon',
    typeProjet: 'Tertiaire',
    surface: 8500,
    coutTotal: 17000000,
    ratioM2: 2000,
    dateCreation: '2022',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Commerce',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Macro-Lot',
      Gamme: 'Haute'
    },
    lots: createProjectLots(8500, 17000000, 'Commerce', '5')
  },
  {
    id: '6',
    projet: 'Bureaux Modernes',
    typeProjet: 'Tertiaire',
    surface: 3200,
    coutTotal: 6400000,
    ratioM2: 2000,
    dateCreation: '2023',
    statut: 'En cours',
    parameters: {
      TypologieProgramme: 'Tertiaire',
      ModaliteProgramme: 'Restructuration',
      DevolutonMarche: 'Contractant Général',
      Gamme: 'Très Haute'
    },
    lots: createProjectLots(3200, 6400000, 'Tertiaire', '6')
  },
  {
    id: '7',
    projet: 'Appartements Neufs',
    typeProjet: 'Résidentiel',
    surface: 2800,
    coutTotal: 4200000,
    ratioM2: 1500,
    dateCreation: '2023',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Logement',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Entreprise Générale',
      Gamme: 'Haute'
    },
    lots: createProjectLots(2800, 4200000, 'Résidentiel', '7')
  },
  {
    id: '8',
    projet: 'Entrepôt Logistique',
    typeProjet: 'Industriel',
    surface: 12000,
    coutTotal: 18000000,
    ratioM2: 1500,
    dateCreation: '2023',
    statut: 'En cours',
    parameters: {
      TypologieProgramme: 'Industrie',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Macro-Lot',
      Gamme: 'Moyenne'
    },
    lots: createProjectLots(12000, 18000000, 'Industrie', '8')
  },
  {
    id: '9',
    projet: 'Hôpital Régional',
    typeProjet: 'Santé',
    surface: 15000,
    coutTotal: 30000000,
    ratioM2: 2000,
    dateCreation: '2022',
    statut: 'En cours',
    parameters: {
      TypologieProgramme: 'Tertiaire',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Corps d\'État Séparés',
      Gamme: 'Très Haute'
    },
    lots: createProjectLots(15000, 30000000, 'Santé', '9')
  },
  {
    id: '10',
    projet: 'École Maternelle',
    typeProjet: 'Éducation',
    surface: 1800,
    coutTotal: 2700000,
    ratioM2: 1500,
    dateCreation: '2023',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Tertiaire',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Entreprise Générale',
      Gamme: 'Haute'
    },
    lots: createProjectLots(1800, 2700000, 'Éducation', '10')
  },
  // --- 18 nouveaux projets pour enrichir la base statistique ---
  {
    id: '11',
    projet: 'Logements Sociaux Bordeaux',
    typeProjet: 'Résidentiel',
    surface: 2200,
    coutTotal: 2816000,
    ratioM2: 1280,
    dateCreation: '2022',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Logement',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Entreprise Générale',
      Gamme: 'Moyenne'
    },
    lots: createProjectLots(2200, 2816000, 'Résidentiel', '11')
  },
  {
    id: '12',
    projet: 'Résidence Étudiante Grenoble',
    typeProjet: 'Résidentiel',
    surface: 1600,
    coutTotal: 1920000,
    ratioM2: 1200,
    dateCreation: '2022',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Logement',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Macro-Lot',
      Gamme: 'Moyenne'
    },
    lots: createProjectLots(1600, 1920000, 'Résidentiel', '12')
  },
  {
    id: '13',
    projet: 'Rénovation Lourde Immeuble Haussmann',
    typeProjet: 'Résidentiel',
    surface: 1800,
    coutTotal: 3510000,
    ratioM2: 1950,
    dateCreation: '2023',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Logement',
      ModaliteProgramme: 'Rénovation Lourde',
      DevolutonMarche: 'Corps d\'État Séparés',
      Gamme: 'Haute'
    },
    lots: createProjectLots(1800, 3510000, 'Résidentiel', '13')
  },
  {
    id: '14',
    projet: 'Résidence Standing Paris 16e',
    typeProjet: 'Résidentiel',
    surface: 5800,
    coutTotal: 9860000,
    ratioM2: 1700,
    dateCreation: '2023',
    statut: 'En cours',
    parameters: {
      TypologieProgramme: 'Logement',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Entreprise Générale',
      Gamme: 'Haute'
    },
    lots: createProjectLots(5800, 9860000, 'Résidentiel', '14')
  },
  {
    id: '15',
    projet: 'Siège Social PME Nantes',
    typeProjet: 'Tertiaire',
    surface: 2400,
    coutTotal: 5040000,
    ratioM2: 2100,
    dateCreation: '2022',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Tertiaire',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Contractant Général',
      Gamme: 'Haute'
    },
    lots: createProjectLots(2400, 5040000, 'Tertiaire', '15')
  },
  {
    id: '16',
    projet: 'Open Space Restructuré Lille',
    typeProjet: 'Tertiaire',
    surface: 3500,
    coutTotal: 6125000,
    ratioM2: 1750,
    dateCreation: '2023',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Tertiaire',
      ModaliteProgramme: 'Restructuration',
      DevolutonMarche: 'Entreprise Générale',
      Gamme: 'Haute'
    },
    lots: createProjectLots(3500, 6125000, 'Tertiaire', '16')
  },
  {
    id: '17',
    projet: 'Lycée Professionnel Toulouse',
    typeProjet: 'Éducation',
    surface: 7000,
    coutTotal: 12950000,
    ratioM2: 1850,
    dateCreation: '2021',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Tertiaire',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Corps d\'État Séparés',
      Gamme: 'Haute'
    },
    lots: createProjectLots(7000, 12950000, 'Éducation', '17')
  },
  {
    id: '18',
    projet: 'Centre Commercial Rénovation Marseille',
    typeProjet: 'Commerce',
    surface: 6000,
    coutTotal: 10800000,
    ratioM2: 1800,
    dateCreation: '2022',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Commerce',
      ModaliteProgramme: 'Rénovation Lourde',
      DevolutonMarche: 'Macro-Lot',
      Gamme: 'Haute'
    },
    lots: createProjectLots(6000, 10800000, 'Commerce', '18')
  },
  {
    id: '19',
    projet: 'Galerie Marchande Strasbourg',
    typeProjet: 'Commerce',
    surface: 4000,
    coutTotal: 6400000,
    ratioM2: 1600,
    dateCreation: '2023',
    statut: 'En cours',
    parameters: {
      TypologieProgramme: 'Commerce',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Entreprise Générale',
      Gamme: 'Moyenne'
    },
    lots: createProjectLots(4000, 6400000, 'Commerce', '19')
  },
  {
    id: '20',
    projet: 'Rénovation Light Boutiques Bordeaux',
    typeProjet: 'Commerce',
    surface: 2000,
    coutTotal: 1800000,
    ratioM2: 900,
    dateCreation: '2024',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Commerce',
      ModaliteProgramme: 'Rénovation Light',
      DevolutonMarche: 'Entreprise Générale',
      Gamme: 'Moyenne'
    },
    lots: createProjectLots(2000, 1800000, 'Commerce', '20')
  },
  {
    id: '21',
    projet: 'Hôtel Business Lyon Confluence',
    typeProjet: 'Hôtel',
    surface: 5500,
    coutTotal: 13200000,
    ratioM2: 2400,
    dateCreation: '2022',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Hôtel',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Contractant Général',
      Gamme: 'Haute'
    },
    lots: createProjectLots(5500, 13200000, 'Hôtel', '21')
  },
  {
    id: '22',
    projet: 'Hôtel Boutique Marais Paris',
    typeProjet: 'Hôtel',
    surface: 2800,
    coutTotal: 8960000,
    ratioM2: 3200,
    dateCreation: '2023',
    statut: 'En cours',
    parameters: {
      TypologieProgramme: 'Hôtel',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Entreprise Générale',
      Gamme: 'Très Haute'
    },
    lots: createProjectLots(2800, 8960000, 'Hôtel', '22')
  },
  {
    id: '23',
    projet: 'Hôtel Budget Réhabilitation Rennes',
    typeProjet: 'Hôtel',
    surface: 3000,
    coutTotal: 4800000,
    ratioM2: 1600,
    dateCreation: '2024',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Hôtel',
      ModaliteProgramme: 'Réhabilitation',
      DevolutonMarche: 'Entreprise Générale',
      Gamme: 'Moyenne'
    },
    lots: createProjectLots(3000, 4800000, 'Hôtel', '23')
  },
  {
    id: '24',
    projet: 'Usine Agroalimentaire Bretagne',
    typeProjet: 'Industrie',
    surface: 8000,
    coutTotal: 11200000,
    ratioM2: 1400,
    dateCreation: '2022',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Industrie',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Entreprise Générale',
      Gamme: 'Haute'
    },
    lots: createProjectLots(8000, 11200000, 'Industrie', '24')
  },
  {
    id: '25',
    projet: 'Entrepôt Frigorifique Normandie',
    typeProjet: 'Industrie',
    surface: 15000,
    coutTotal: 16500000,
    ratioM2: 1100,
    dateCreation: '2023',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Industrie',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Macro-Lot',
      Gamme: 'Haute'
    },
    lots: createProjectLots(15000, 16500000, 'Industrie', '25')
  },
  {
    id: '26',
    projet: 'Atelier de Production Alsace',
    typeProjet: 'Industrie',
    surface: 5000,
    coutTotal: 5250000,
    ratioM2: 1050,
    dateCreation: '2024',
    statut: 'En cours',
    parameters: {
      TypologieProgramme: 'Industrie',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Entreprise Générale',
      Gamme: 'Moyenne'
    },
    lots: createProjectLots(5000, 5250000, 'Industrie', '26')
  },
  {
    id: '27',
    projet: 'Clinique Privée Aix-en-Provence',
    typeProjet: 'Santé',
    surface: 9000,
    coutTotal: 23400000,
    ratioM2: 2600,
    dateCreation: '2022',
    statut: 'Terminé',
    parameters: {
      TypologieProgramme: 'Tertiaire',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Corps d\'État Séparés',
      Gamme: 'Très Haute'
    },
    lots: createProjectLots(9000, 23400000, 'Santé', '27')
  },
  {
    id: '28',
    projet: 'EHPAD Maison de Retraite Reims',
    typeProjet: 'Santé',
    surface: 4200,
    coutTotal: 9240000,
    ratioM2: 2200,
    dateCreation: '2023',
    statut: 'En cours',
    parameters: {
      TypologieProgramme: 'Tertiaire',
      ModaliteProgramme: 'Neuf',
      DevolutonMarche: 'Corps d\'État Séparés',
      Gamme: 'Haute'
    },
    lots: createProjectLots(4200, 9240000, 'Santé', '28')
  }
];

export const ratioData: RatioData[] = rawRatioData.map(enrichWithUnits);
